// Scenario catalogue, presets and the agent-driven playbook (static reference data).

export const docTypes = [
  { id: "invoice", label: "Tax Invoice" },
  { id: "credit_note", label: "Credit Note" },
  { id: "debit_note", label: "Debit Note" },
];

export const currencyOptions = ["AED", "USD", "EUR", "GBP", "SAR", "INR"];

export const scenarioOptions = [
  { id: "", label: "Auto-detect" },
  { id: "b2b", label: "Domestic B2B (5%)" },
  { id: "b2c", label: "Domestic B2C (no TRN)" },
  { id: "cash", label: "Cash Sale" },
  { id: "b2g", label: "Government (B2G)" },
  { id: "marketplace", label: "Marketplace" },
  { id: "export", label: "Export / Overseas (0%)" },
  { id: "rcm_import", label: "Import - Reverse Charge" },
  { id: "zero", label: "Zero-rated (0%)" },
  { id: "exempt", label: "Exempt" },
  { id: "out_of_scope", label: "Out-of-scope" },
  { id: "mixed", label: "Mixed-rate" },
];

export const vatCategories = [
  { id: "", label: "Auto" },
  { id: "standard", label: "Standard 5%" },
  { id: "zero", label: "Zero 0%" },
  { id: "exempt", label: "Exempt" },
  { id: "export", label: "Export/OOS" },
  { id: "rcm", label: "Reverse charge" },
];

// One-click presets that fill the generator with realistic data for each scenario.
export const presets = [
  { id: "b2b", label: "Standard B2B", scenario: "b2b", currency: "AED",
    buyer: { name: "Nova Retail LLC", trn: "100999888700001", country: "United Arab Emirates", type: "business" },
    items: [{ description: "Consulting services", quantity: 2, unit_price: 1000 }] },
  { id: "b2c", label: "B2C (no TRN)", scenario: "b2c", currency: "AED",
    buyer: { name: "Walk-in Customer", trn: "", country: "United Arab Emirates", type: "individual" },
    items: [{ description: "Retail goods", quantity: 1, unit_price: 350 }] },
  { id: "cash", label: "Cash sale", scenario: "cash", currency: "AED",
    buyer: { name: "Cash Customer", trn: "", country: "United Arab Emirates", type: "individual" },
    items: [{ description: "POS sale", quantity: 3, unit_price: 75 }] },
  { id: "b2g", label: "Government", scenario: "b2g", currency: "AED",
    buyer: { name: "Ministry of Finance", trn: "100111222300004", country: "United Arab Emirates", type: "government" },
    items: [{ description: "IT maintenance contract", quantity: 1, unit_price: 50000 }] },
  { id: "marketplace", label: "Marketplace", scenario: "marketplace", currency: "AED", principal: "Seller Partner FZC",
    buyer: { name: "Online Buyer", trn: "", country: "United Arab Emirates", type: "individual" },
    items: [{ description: "Marketplace order #4821", quantity: 1, unit_price: 240 }] },
  { id: "export", label: "Export / Overseas", scenario: "export", currency: "USD",
    buyer: { name: "Britannia Imports Ltd", trn: "", country: "United Kingdom", type: "business" },
    items: [{ description: "Exported equipment", quantity: 1, unit_price: 5000 }] },
  { id: "rcm_import", label: "Import (RCM)", scenario: "rcm_import", currency: "EUR",
    buyer: { name: "Acme FZC (self)", trn: "100123456700003", country: "United Arab Emirates", type: "business" },
    items: [{ description: "Imported software licence", quantity: 1, unit_price: 3000 }] },
  { id: "zero", label: "Zero-rated", scenario: "zero", currency: "AED",
    buyer: { name: "Gulf Medical Center", trn: "100222333400005", country: "United Arab Emirates", type: "business" },
    items: [{ description: "Healthcare supplies", quantity: 10, unit_price: 120 }] },
  { id: "exempt", label: "Exempt", scenario: "exempt", currency: "AED",
    buyer: { name: "Emirates Bank PJSC", trn: "100333444500006", country: "United Arab Emirates", type: "business" },
    items: [{ description: "Financial service fee", quantity: 1, unit_price: 800 }] },
  { id: "mixed", label: "Mixed-rate", scenario: "mixed", currency: "AED",
    buyer: { name: "Oasis Trading LLC", trn: "100444555600007", country: "United Arab Emirates", type: "business" },
    items: [
      { description: "Standard goods", quantity: 2, unit_price: 500, vat_category: "standard" },
      { description: "Zero-rated books", quantity: 5, unit_price: 60, vat_category: "zero" },
      { description: "Exempt service", quantity: 1, unit_price: 300, vat_category: "exempt" },
    ] },
];

export const playbook = [
  { id: "standard", title: "Send a Standard e-Invoice", scenario: "b2b",
    desc: "Issue a taxable 5% B2B supply across the Peppol 5-corner model.",
    steps: ["Agent 1 classifies the outbound intent", "Agent 5 applies UAE 5% VAT", "Agent 6 builds PINT-AE XML", "Agent 8 routes C2->C3->C4", "Agent 10 reports TDD to FTA (C5)"], agents: [1, 5, 6, 8, 10] },
  { id: "b2c", title: "Buyers Who Do Not Require an e-Invoice", scenario: "b2c",
    desc: "B2C buyers without a TRN receive a compliant PDF/email instead of a network invoice.",
    steps: ["Agent 3 detects no buyer TRN -> B2C schema", "Agent 8 switches to PDF/email delivery", "Agent 10 still reports the supply to FTA"], agents: [3, 8, 10] },
  { id: "export", title: "Export / Overseas Buyers", scenario: "export",
    desc: "Cross-border supplies are zero-rated / out-of-scope with the correct exemption code.",
    steps: ["Agent 5 sets 0% with VATEX-AE-EXPORT code", "Agent 6 maps exemption tags", "Foreign currency auto-converted to AED"], agents: [5, 6, 3] },
  { id: "rcm", title: "Imports & Reverse Charge (RCM)", scenario: "rcm_import",
    desc: "Import supplies where VAT liability shifts to the buyer (both directions supported).",
    steps: ["Agent 5 flags reverse charge", "Output tax shown as 0, accounted by buyer", "Reported in VAT return Box 2"], agents: [5, 11] },
  { id: "b2g", title: "Government Buyers (B2G)", scenario: "b2g",
    desc: "Invoices to UAE government entities route to the government ASP with procurement data.",
    steps: ["Agent 3 enriches government entity", "Agent 8 routes to government ASP", "Agent 10 confirms MLS"], agents: [3, 8, 10] },
  { id: "marketplace", title: "Marketplace Transactions", scenario: "marketplace",
    desc: "Third-party/agent invoicing where VAT liability stays with the principal supplier.",
    steps: ["Capture principal details", "Agent 5 reports principal's tax payload", "Avoids remittance mismatch at FTA"], agents: [3, 5, 10] },
  { id: "cash", title: "Cash Sales", scenario: "cash",
    desc: "POS / cash receipts recorded and reported with no buyer matching required.",
    steps: ["Agent 1 classifies cash sale", "Agent 9 skips buyer matching", "Agent 10 reports supply"], agents: [1, 9, 10] },
  { id: "backdated", title: "Backdated Invoices", scenario: "b2b",
    desc: "Set an earlier issue date; the agent flags it against UAE time-of-supply rules.",
    steps: ["Pick an earlier issue date", "Agent 2 flags 'backdated'", "Reported into the correct tax period"], agents: [2, 4, 11] },
  { id: "items", title: "Items That Must Generate an e-Invoice", scenario: "b2b",
    desc: "Any taxable supply line automatically triggers e-invoice generation.",
    steps: ["Add line items", "Agent 4 validates mandatory fields", "Agent 6 generates the document"], agents: [4, 6] },
  { id: "credit", title: "Send a Credit Note e-Invoice", docType: "credit_note",
    desc: "Reduce a previously submitted invoice (e.g. returned goods) with a referenced credit note.",
    steps: ["Choose Credit Note + reference number", "Negative values mapped", "Both docs accepted by FTA"], agents: [5, 6, 10] },
  { id: "debit", title: "Debit Note Adjustments", docType: "debit_note",
    desc: "Increase the value/tax of goods already delivered with a referenced debit note.",
    steps: ["Choose Debit Note + reference", "Tax liability increased", "Reported to FTA"], agents: [5, 6, 10] },
  { id: "cancel", title: "Cancel an e-Invoice", action: "cancel",
    desc: "Cancel a submitted invoice; the cancellation is reported to the FTA and trace-logged.",
    steps: ["Open Status & Trace", "Cancel the document", "Agent 10 reports cancellation (MLS)"], agents: [10, 12] },
  { id: "batch", title: "Batch Approval for Submission", route: "/a/batch",
    desc: "Agent auto-approves & submits many documents at once - fully audit-logged.",
    steps: ["Open Batch Approval", "Select documents", "Agent auto-approves & submits"], agents: [9, 10, 12] },
  { id: "trace", title: "Track & Trace Status History", route: "/a/status",
    desc: "Follow each document corner-by-corner (C1->C5) with MLS / TDD logs.",
    steps: ["Open Status & Trace", "Expand a document", "View the 5-corner event timeline"], agents: [8, 10, 12] },
  { id: "recon", title: "Supplier Statement Matching", route: "/a/reconciliation",
    desc: "Agent extracts supplier statements and reconciles them against your books (2/3-way).",
    steps: ["Open Supplier Matching", "Run reconciliation", "Review matched & exceptions"], agents: [11, 9] },
  { id: "approval", title: "Overview of Approval Workflow", route: "/a/batch",
    desc: "Zero human-in-the-loop: agents auto-approve, with an optional admin confirmation screen.",
    steps: ["Generated docs are auto-approved", "Optional admin review in Batch Approval", "Every action is audit-logged"], agents: [9, 12] },
];
